import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { money, fileUrl } from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { PhotoUpload } from "@/components/PhotoUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { WO_STATUSES } from "@/lib/constants";
import { ArrowLeft, Loader2, MapPin, KeyRound, User, Check, X, LogIn, CheckCircle2, DollarSign, FileText, Send, MessageSquare, Sparkles, AlertTriangle, Users, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { FieldPanel, SyncStatusBar } from "@/pages/contractor/FieldPanel";
import { JobCompliance } from "@/pages/contractor/JobCompliance";
import { AdminCompliancePanel } from "@/components/AdminCompliancePanel";
import { EditWorkOrderDialog } from "@/components/EditWorkOrderDialog";

function TRow({ k, v }) {
  return v ? <div><span className="text-xs uppercase font-bold text-slate-400">{k}</span><div className="text-slate-800">{v}</div></div> : null;
}

function AITriagePanel({ woId, triage, onDone }) {
  const [t, setT] = useState(triage || null);
  const [recs, setRecs] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadRecs = () => api.get(`/work-orders/${woId}/recommendations`).then(({ data }) => setRecs(data.recommendations || data.fallback || [])).catch(() => setRecs([]));
  useEffect(() => { if (!t) api.get(`/work-orders/${woId}/triage`).then(({ data }) => setT(data)).catch(() => {}); loadRecs(); }, []); // eslint-disable-line

  const rerun = async () => { setBusy(true); try { const { data } = await api.post(`/work-orders/${woId}/triage`); setT(data); toast.success("AI triage refreshed"); } catch (e) { toast.error("Failed"); } setBusy(false); };
  const assign = async (cid) => { try { await api.put(`/work-orders/${woId}/assign`, { contractor_id: cid }); toast.success("Contractor assigned"); onDone?.(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const offer = async (cid) => { try { await api.post(`/work-orders/${woId}/offer`, { contractor_ids: [cid] }); toast.success("Offer sent"); onDone?.(); } catch (e) { toast.error("Failed"); } };

  const badge = (label, on, color) => on ? <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${color}`}>{label}</span> : null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="ai-triage-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-500" /> AI Triage</h3>
        <Button size="sm" variant="ghost" disabled={busy} onClick={rerun} data-testid="ai-rerun-btn">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}</Button>
      </div>
      {!t || t.status === "pending" ? <p className="text-sm text-slate-400">Triage pending…</p> : t.status === "unavailable" ? (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3" data-testid="ai-unavailable">AI unavailable — manual review required. {t.reason ? "" : ""}</div>
      ) : (
        <div className="space-y-2 text-sm">
          {(t.emergency_risk || t.human_review_required) && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-2 font-semibold" data-testid="ai-emergency-flag"><AlertTriangle className="w-4 h-4" /> {t.emergency_risk ? "Emergency risk — immediate SJ review" : "Human review required"}</div>
          )}
          <TRow k="Trade" v={t.recommended_trade} />
          <TRow k="Category" v={t.recommended_category} />
          <TRow k="Urgency" v={t.recommended_urgency} />
          <TRow k="Likely issue" v={t.likely_issue} />
          <TRow k="First action" v={t.recommended_first_action} />
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {badge("Photos needed", t.photos_needed, "bg-blue-100 text-blue-700")}
            {badge("Human review", t.human_review_required, "bg-amber-100 text-amber-700")}
            {t.overridden && badge("Overridden", true, "bg-slate-200 text-slate-700")}
          </div>
          <div className="text-xs text-slate-400 pt-1">Confidence {t.confidence}% · {t.reasoning}</div>
          {t.overridden && <div className="text-xs text-slate-400">Overridden by {t.overridden_by}{t.override_reason ? ` — ${t.override_reason}` : ""}</div>}
        </div>
      )}

      <div className="mt-5 border-t border-slate-100 pt-4">
        <h4 className="font-head font-semibold text-slate-900 text-sm mb-2 flex items-center gap-2"><Users className="w-4 h-4 text-indigo-500" /> Recommended Contractors</h4>
        {!recs ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : recs.length === 0 ? <p className="text-sm text-slate-400">No eligible contractors found.</p> : (
          <div className="space-y-2" data-testid="ai-recommendations">
            {recs.slice(0, 5).map((r) => (
              <div key={r.contractor_id} className={`rounded-lg border p-3 ${r.eligible ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`reco-${r.contractor_id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900 text-sm">{r.name || r.company_name} {r.match != null && <span className="text-indigo-600 font-semibold">— {r.match}%</span>}</div>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.doc_status === "valid" ? "bg-emerald-100 text-emerald-700" : r.doc_status === "expiring" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{r.doc_status || "—"}</span>
                </div>
                {r.reasons?.length > 0 && <div className="text-xs text-slate-500 mt-1">{r.reasons.join(" · ")}</div>}
                {r.warnings?.length > 0 && <div className="text-xs text-amber-600 mt-0.5">⚠ {r.warnings.join(" · ")}</div>}
                {r.eligible !== undefined && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" disabled={!r.eligible} onClick={() => assign(r.contractor_id)} data-testid={`assign-${r.contractor_id}`}>Assign</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!r.eligible} onClick={() => offer(r.contractor_id)} data-testid={`offer-${r.contractor_id}`}>Send Offer</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const staff = user.role === "dispatcher" || user.role === "admin";

  const load = () => api.get(`/work-orders/${id}`).then(({ data }) => { setData(data); setTick((n) => n + 1); }).catch(() => setData(false));
  useEffect(() => { load(); }, [id]);

  if (data === null) return <Shell user={user}><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div></Shell>;
  if (!data) return <Shell user={user}><p>Not found or access denied.</p></Shell>;

  const wo = data.work_order;
  const beforePhotos = data.photos.filter((p) => p.phase === "before");
  const progressPhotos = data.photos.filter((p) => p.phase === "progress");
  const afterPhotos = data.photos.filter((p) => p.phase === "after");
  const assignedToMe = user.role === "contractor" && wo.contractor;

  return (
    <Shell user={user}>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 mb-4 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-head text-2xl font-semibold text-slate-900">{wo.wo_number}</h2>
                <div className="mt-2 flex items-center gap-2 flex-wrap"><StatusBadge status={wo.status} /><UrgencyBadge urgency={wo.urgency} /><span className="text-sm text-slate-500">{wo.service_category}</span></div>
              </div>
            </div>
            <p className="mt-4 text-slate-700">{wo.problem_description}</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
              {wo.property && <div><div className="text-xs uppercase font-bold text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> Property</div><div className="mt-1 text-slate-800">{wo.property.name}<br />{(user.role === "contractor" && !assignedToMe) ? `${wo.property.city}, ${wo.property.state}` : `${wo.property.street_address || ""} ${wo.property.city}, ${wo.property.state}`}</div></div>}
              {wo.requested_date && <div><div className="text-xs uppercase font-bold text-slate-400">Requested Date</div><div className="mt-1 text-slate-800">{wo.requested_date}</div></div>}
              {wo.scheduled_date && <div><div className="text-xs uppercase font-bold text-slate-400">Scheduled</div><div className="mt-1 text-slate-800">{wo.scheduled_date}</div></div>}
              {(assignedToMe || staff) && wo.access_instructions && <div><div className="text-xs uppercase font-bold text-slate-400 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Access</div><div className="mt-1 text-slate-800">{wo.access_instructions}</div></div>}
              {staff && wo.contractor && <div><div className="text-xs uppercase font-bold text-slate-400 flex items-center gap-1"><User className="w-3 h-3" /> Contractor</div><div className="mt-1 text-slate-800">{wo.contractor.first_name} {wo.contractor.last_name} ({wo.contractor.trade})</div></div>}
              {staff && wo.client && <div><div className="text-xs uppercase font-bold text-slate-400">Client</div><div className="mt-1 text-slate-800">{wo.client.company_name}</div></div>}
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-4">Photos</h3>
            <PhotoSection title="Before" photos={beforePhotos} phase="before" wo={wo} user={user} assignedToMe={assignedToMe} staff={staff} onUploaded={load} />
            <PhotoSection title="Progress" photos={progressPhotos} phase="progress" wo={wo} user={user} assignedToMe={assignedToMe} staff={staff} onUploaded={load} />
            <PhotoSection title="After" photos={afterPhotos} phase="after" wo={wo} user={user} assignedToMe={assignedToMe} staff={staff} onUploaded={load} />
          </div>

          {/* Notes */}
          <NotesSection woId={wo.id} notes={data.notes} staff={staff} onAdded={load} />
        </div>

        {/* Sidebar actions */}
        <div className="space-y-6">
          {user.role === "contractor" && wo.assigned_contractor_id === user.id && <JobCompliance wo={wo} refreshKey={tick} onChange={load} />}
          {user.role === "contractor" && <ContractorActions wo={wo} onDone={load} />}
          {staff && <AdminCompliancePanel wo={wo} onDone={load} />}
          {staff && <AITriagePanel woId={wo.id} triage={wo.ai_triage} onDone={load} />}
          {staff && <DispatchActions wo={wo} invoiceExists={(data.invoices || []).length > 0} onDone={load} />}
          {(staff) && wo.customer_price != null && <FinancialsCard wo={wo} />}
          {data.estimates?.length > 0 && <EstimatesCard estimates={data.estimates} />}

          {/* Timeline */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-4">Timeline</h3>
            <ol className="space-y-3">
              {[...(wo.timeline || [])].reverse().map((t, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 shrink-0" />
                  <div>
                    <div className="font-medium text-slate-800">{t.status}{t.by_name ? <span className="font-normal text-slate-500"> · {t.by_name}</span> : null}</div>
                    <div className="text-xs text-slate-400">{new Date(t.at).toLocaleString()}</div>
                    {Array.isArray(t.changes) && t.changes.length > 0 && (
                      <ul className="mt-1 space-y-0.5" data-testid={`timeline-changes-${i}`}>
                        {t.changes.map((c, ci) => (
                          <li key={ci} className="text-xs text-slate-500"><span className="font-medium text-slate-600">{c.label}:</span> <span className="line-through text-slate-400">{c.old}</span> → <span className="text-slate-700">{c.new}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ user, children }) {
  return <DashboardLayout nav={getNav(user.role)} title="Work Order" bottomNav={user.role === "contractor"}>{children}</DashboardLayout>;
}

function PhotoSection({ title, photos, phase, wo, user, assignedToMe, staff, onUploaded }) {
  const canUpload = assignedToMe || staff;
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700">{title} ({photos.length})</span>
        {canUpload && <PhotoUpload workOrderId={wo.id} phase={phase} onUploaded={onUploaded} />}
      </div>
      {photos.length === 0 ? <p className="text-xs text-slate-400">No {title.toLowerCase()} photos.</p> : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((p) => (
            <a key={p.id} href={fileUrl(p.storage_path)} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-slate-200">
              <img src={fileUrl(p.storage_path)} alt={phase} className="w-full h-24 object-cover" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function ContractorActions({ wo, onDone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
      <h3 className="font-head font-semibold text-slate-900">Estimate</h3>
      <ContractorEstimateDialog wo={wo} onDone={onDone} />
    </div>
  );
}

function ContractorEstimateDialog({ wo, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ labor_cost: "", material_cost: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/estimates/contractor", { work_order_id: wo.id, labor_cost: parseFloat(f.labor_cost || 0), material_cost: parseFloat(f.material_cost || 0), notes: f.notes }); toast.success("Estimate submitted"); setOpen(false); onDone(); }
    catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="submit-estimate-btn" variant="outline" className="w-full"><FileText className="w-4 h-4 mr-1" /> Submit Estimate</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Submit Estimate</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Labor Cost</Label><Input data-testid="est-labor" type="number" value={f.labor_cost} onChange={(e) => setF({ ...f, labor_cost: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Material Cost</Label><Input data-testid="est-material" type="number" value={f.material_cost} onChange={(e) => setF({ ...f, material_cost: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Notes</Label><Textarea data-testid="est-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" /></div>
          <Button data-testid="est-submit" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700">Submit</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DispatchActions({ wo, invoiceExists, onDone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
      <h3 className="font-head font-semibold text-slate-900">Dispatch Actions</h3>
      <EditWorkOrderDialog wo={wo} invoiceExists={invoiceExists} onDone={onDone} />
      <AssignDialog wo={wo} onDone={onDone} />
      <PricingDialog wo={wo} onDone={onDone} />
      <CustomerEstimateDialog wo={wo} onDone={onDone} />
      <InvoiceDialog wo={wo} onDone={onDone} />
      <StatusDialog wo={wo} onDone={onDone} />
    </div>
  );
}

function AssignDialog({ wo, onDone }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [cid, setCid] = useState("");
  const [date, setDate] = useState("");
  const [payout, setPayout] = useState("");
  const [zeroConfirm, setZeroConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPayout(wo.contractor_payout || ""); setZeroConfirm(false); api.get("/contractors", { params: { status: "Active" } }).then(({ data }) => setContractors(data)); } }, [open, wo.contractor_payout]);
  const submit = async (confirmedZero = false) => {
    if (!cid) return toast.error("Select contractor");
    if (isAdmin) {
      if (payout === "" || isNaN(Number(payout)) || Number(payout) < 0) return toast.error("Enter a valid contractor payout");
      if (Number(payout) === 0 && !confirmedZero) { setZeroConfirm(true); return; }
    }
    setBusy(true);
    try {
      const body = { contractor_id: cid, scheduled_date: date };
      if (isAdmin) body.contractor_payout = Number(payout);
      await api.put(`/work-orders/${wo.id}/assign`, body);
      toast.success("Contractor assigned successfully.");
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="assign-contractor-btn" className="w-full bg-blue-600 hover:bg-blue-700"><User className="w-4 h-4 mr-1" /> {wo.assigned_contractor_id ? "Reassign" : "Assign"} Contractor</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Assign Contractor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Contractor</Label>
            <Select value={cid} onValueChange={setCid}><SelectTrigger data-testid="assign-select" className="mt-1.5"><SelectValue placeholder="Select active contractor" /></SelectTrigger>
              <SelectContent>{contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.trade}</SelectItem>)}</SelectContent></Select>
          </div>
          {isAdmin && <div><Label>Contractor Payout</Label><Input data-testid="assign-detail-payout" type="number" value={payout} onChange={(e) => setPayout(e.target.value)} className="mt-1.5" /></div>}
          <div><Label>Schedule Date (optional)</Label><Input data-testid="assign-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
          {zeroConfirm ? (
            <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="zero-payout-confirm">
              <p className="text-amber-800">Contractor payout is $0. Continue?</p>
              <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => setZeroConfirm(false)}>Cancel</Button><Button size="sm" data-testid="confirm-zero-payout" onClick={() => submit(true)} className="bg-amber-600 hover:bg-amber-700">Continue</Button></div>
            </div>
          ) : (
            <Button data-testid="assign-submit" disabled={busy} onClick={() => submit()} className="w-full bg-blue-600 hover:bg-blue-700">Assign</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PricingDialog({ wo, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ contractor_payout: wo.contractor_payout || "", material_cost: wo.material_cost || "", other_cost: wo.other_cost || "", customer_price: wo.customer_price || "" });
  const [busy, setBusy] = useState(false);
  const cp = parseFloat(f.customer_price || 0), cost = parseFloat(f.contractor_payout || 0) + parseFloat(f.material_cost || 0) + parseFloat(f.other_cost || 0);
  const gp = cp - cost, margin = cp ? (gp / cp * 100).toFixed(1) : 0;
  const submit = async () => { setBusy(true); try { await api.put(`/work-orders/${wo.id}/pricing`, { contractor_payout: parseFloat(f.contractor_payout || 0), material_cost: parseFloat(f.material_cost || 0), other_cost: parseFloat(f.other_cost || 0), customer_price: parseFloat(f.customer_price || 0) }); toast.success("Pricing saved"); setOpen(false); onDone(); } catch (e) { toast.error("Failed"); } setBusy(false); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="set-pricing-btn" variant="outline" className="w-full"><DollarSign className="w-4 h-4 mr-1" /> Set Pricing & Margin</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Pricing & Margin</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Contractor Payout</Label><Input data-testid="price-payout" type="number" value={f.contractor_payout} onChange={(e) => setF({ ...f, contractor_payout: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Material Cost</Label><Input data-testid="price-material" type="number" value={f.material_cost} onChange={(e) => setF({ ...f, material_cost: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Other Cost</Label><Input data-testid="price-other" type="number" value={f.other_cost} onChange={(e) => setF({ ...f, other_cost: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Customer Price</Label><Input data-testid="price-customer" type="number" value={f.customer_price} onChange={(e) => setF({ ...f, customer_price: e.target.value })} className="mt-1.5" /></div>
          <div className="rounded-lg bg-slate-50 p-3 text-sm flex justify-between"><span>Gross Profit: <strong className="text-green-600">{money(gp)}</strong></span><span>Margin: <strong>{margin}%</strong></span></div>
          <Button data-testid="price-submit" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700">Save Pricing</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CustomerEstimateDialog({ wo, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ scope_of_work: "", labor: "", materials: "", additional_fees: "", tax: "", expiration_date: "" });
  const [busy, setBusy] = useState(false);
  const total = ["labor", "materials", "additional_fees", "tax"].reduce((s, k) => s + parseFloat(f[k] || 0), 0);
  const submit = async () => { if (!f.scope_of_work) return toast.error("Add scope"); setBusy(true); try { await api.post("/estimates", { work_order_id: wo.id, scope_of_work: f.scope_of_work, items: [], labor: parseFloat(f.labor || 0), materials: parseFloat(f.materials || 0), additional_fees: parseFloat(f.additional_fees || 0), tax: parseFloat(f.tax || 0), expiration_date: f.expiration_date }); toast.success("Estimate sent to client"); setOpen(false); onDone(); } catch (e) { toast.error("Failed"); } setBusy(false); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-estimate-btn" variant="outline" className="w-full"><FileText className="w-4 h-4 mr-1" /> Create Customer Estimate</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">Customer Estimate</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Scope of Work</Label><Textarea data-testid="cest-scope" value={f.scope_of_work} onChange={(e) => setF({ ...f, scope_of_work: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Labor</Label><Input data-testid="cest-labor" type="number" value={f.labor} onChange={(e) => setF({ ...f, labor: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Materials</Label><Input data-testid="cest-materials" type="number" value={f.materials} onChange={(e) => setF({ ...f, materials: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Additional Fees</Label><Input data-testid="cest-fees" type="number" value={f.additional_fees} onChange={(e) => setF({ ...f, additional_fees: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Tax</Label><Input data-testid="cest-tax" type="number" value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label>Expiration Date</Label><Input data-testid="cest-exp" type="date" value={f.expiration_date} onChange={(e) => setF({ ...f, expiration_date: e.target.value })} className="mt-1.5" /></div>
          <div className="text-right font-head font-semibold">Total: {money(total)}</div>
          <Button data-testid="cest-submit" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700"><Send className="w-4 h-4 mr-1" /> Send to Client</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDialog({ wo, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ description: wo.service_category + " service", labor: wo.customer_price || "", materials: "", additional_charges: "", tax: "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const total = ["labor", "materials", "additional_charges", "tax"].reduce((s, k) => s + parseFloat(f[k] || 0), 0);
  const submit = async () => { setBusy(true); try { await api.post("/invoices", { work_order_id: wo.id, service_date: new Date().toISOString().slice(0, 10), description: f.description, labor: parseFloat(f.labor || 0), materials: parseFloat(f.materials || 0), additional_charges: parseFloat(f.additional_charges || 0), tax: parseFloat(f.tax || 0), due_date: f.due_date }); toast.success("Invoice generated"); setOpen(false); onDone(); } catch (e) { toast.error("Failed"); } setBusy(false); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="generate-invoice-btn" variant="outline" className="w-full">Generate Invoice</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">Generate Invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Description</Label><Input data-testid="inv-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Labor</Label><Input data-testid="inv-labor" type="number" value={f.labor} onChange={(e) => setF({ ...f, labor: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Materials</Label><Input data-testid="inv-materials" type="number" value={f.materials} onChange={(e) => setF({ ...f, materials: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Additional</Label><Input data-testid="inv-additional" type="number" value={f.additional_charges} onChange={(e) => setF({ ...f, additional_charges: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Tax</Label><Input data-testid="inv-tax" type="number" value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label>Due Date</Label><Input data-testid="inv-due" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="mt-1.5" /></div>
          <div className="text-right font-head font-semibold">Total: {money(total)}</div>
          <Button data-testid="inv-submit" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700">Generate</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({ wo, onDone }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(wo.status);
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); try { await api.put(`/work-orders/${wo.id}/status`, { status }); toast.success("Status updated"); setOpen(false); onDone(); } catch (e) { toast.error("Failed"); } setBusy(false); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="change-status-btn" variant="outline" className="w-full">Change Status</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Update Status</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">{WO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Button data-testid="status-submit" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700">Update</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FinancialsCard({ wo }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-600" /> Internal Financials</h3>
      <div className="space-y-2 text-sm">
        <Row l="Contractor Payout" v={money(wo.contractor_payout)} />
        <Row l="Material Cost" v={money(wo.material_cost)} />
        <Row l="Other Cost" v={money(wo.other_cost)} />
        <Row l="Customer Price" v={money(wo.customer_price)} bold />
        <div className="border-t pt-2 flex justify-between font-semibold text-green-600"><span>Gross Profit</span><span>{money(wo.gross_profit)}</span></div>
        <div className="flex justify-between font-semibold"><span>Gross Margin</span><span>{wo.gross_margin}%</span></div>
      </div>
    </div>
  );
}

function EstimatesCard({ estimates }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h3 className="font-head font-semibold text-slate-900 mb-3">Estimates</h3>
      <div className="space-y-2">
        {estimates.map((e) => (
          <div key={e.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-lg p-3">
            <div><div className="font-medium">{e.estimate_number}</div><div className="text-xs text-slate-400 capitalize">{e.estimate_type}</div></div>
            <div className="text-right"><div className="font-semibold">{money(e.total)}</div><StatusBadge status={e.status} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotesSection({ woId, notes, staff, onAdded }) {
  const { isViewer } = useAuth();
  const [text, setText] = useState("");
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const add = async () => { if (!text.trim()) return; setBusy(true); try { await api.post("/notes", { work_order_id: woId, text, internal }); setText(""); onAdded(); } catch (e) { toast.error("Failed"); } setBusy(false); };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Notes</h3>
      <div className="space-y-3 mb-4">
        {notes.length === 0 && <p className="text-sm text-slate-400">No notes yet.</p>}
        {notes.map((n) => (
          <div key={n.id} className={`rounded-lg p-3 text-sm ${n.internal ? "bg-amber-50 border border-amber-100" : "bg-slate-50"}`}>
            <div className="flex items-center gap-2 mb-1"><span className="font-medium text-slate-800">{n.author_name}</span><span className="text-xs text-slate-400 capitalize">{n.author_role}</span>{n.internal && <span className="text-[10px] font-bold text-amber-700 uppercase">Internal</span>}</div>
            <p className="text-slate-600">{n.text}</p>
          </div>
        ))}
      </div>
      {!isViewer && (
        <>
          <Textarea data-testid="note-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note..." rows={2} />
          <div className="mt-2 flex items-center justify-between">
            {staff ? <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="note-internal" /> Internal only</label> : <span />}
            <Button data-testid="add-note-btn" size="sm" disabled={busy} onClick={add} className="bg-blue-600 hover:bg-blue-700">Add Note</Button>
          </div>
        </>
      )}
    </div>
  );
}

const Row = ({ l, v, bold }) => <div className={`flex justify-between ${bold ? "font-semibold text-slate-900" : "text-slate-600"}`}><span>{l}</span><span>{v}</span></div>;

